import { useState, useCallback } from 'react';

export type GroupSelectionId = string | number;

/** Return value of the `useGroupSelection` hook */
export type GroupSelectionHookResult = {
  /** Used to select a group of ids, does not toggle if already selected */
  selectByIds: (ids: GroupSelectionId[]) => void;

  /** Deselects all items selected by the hook */
  deselectAll: () => void;

  /** List of ids of selected items */
  selected: GroupSelectionId[];

  /** Toggles the selection of a single item */
  toggleSelection: (id: GroupSelectionId) => void;
};

/**
 * Allows for tracking of selections by unique ids
 * Provides functions for selecting or deselcting items in the group
 */
export const useGroupSelection = (
  defaultSelected?: GroupSelectionId[],
): GroupSelectionHookResult => {
  const [selected, setSelected] = useState<GroupSelectionId[]>(defaultSelected || []);
  const toggleSelection = useCallback(
    (id: GroupSelectionId) => {
      const selectedSet = new Set(selected);
      if (selectedSet.has(id)) {
        selectedSet.delete(id);
      } else {
        selectedSet.add(id);
      }
      setSelected([...selectedSet]);
    },
    [selected],
  );
  const selectByIds = useCallback((ids: GroupSelectionId[]) => setSelected(ids), []);
  const deselectAll = useCallback(() => setSelected([]), []);

  return {
    selected,
    toggleSelection,
    selectByIds,
    deselectAll,
  };
};
